import React from 'react'
import { TooltipList, TooltipLabel } from '../../../design-system'
import { Tag } from '../../../legacy-design-system/tags'
import { css, cx } from '@emotion/css'
import { AttackType } from '../../attack-status'
import { theme } from '../../../styled'
import { readOnlyGridCss } from '../../../legacy-design-system/text'
import { FormattedMessage } from 'react-intl'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { Link } from '@contrast-security-inc/design-system-typography'

export function AttackerCell({
  onAttackerClick,
  isActiveEvent = false,
  rowData: { sourceIP, attackType, attackers, sourceName, tags }
}: {
  readonly onAttackerClick: () => void
  readonly isActiveEvent?: boolean
  readonly rowData: {
    readonly sourceIP?: string
    readonly attackType: AttackType
    readonly attackers: string[]
    readonly sourceName?: string
    readonly tags: string[]
  }
}) {
  return (
    <Tooltip
      body={<TooltipContent attackType={attackType} attackers={attackers} />}
      placement="right"
      interactive={true}
      className={css`
        z-index: 101;
      `}
    >
      <AttackerCellContent
        onAttackerClick={onAttackerClick}
        isActiveEvent={isActiveEvent}
        sourceIP={sourceIP}
        sourceName={sourceName}
        tags={tags}
      />
    </Tooltip>
  )
}

interface AttackerCellContentProps {
  onAttackerClick: () => void
  isActiveEvent?: boolean
  sourceIP?: string
  sourceName?: string
  tags: string[]
}

const AttackerCellContent = React.forwardRef<
  HTMLDivElement,
  AttackerCellContentProps
>(
  (
    { onAttackerClick, isActiveEvent, sourceIP, sourceName, tags, ...rest },
    ref
  ) => {
    return (
      <div
        className={css`
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding-top: 2px;
        `}
      >
        <div
          className={css`
            display: flex;
            align-items: center;
            ${!!sourceName && !!sourceIP && 'padding-bottom: 3px;'};
            width: 100%;
            margin-right: 20px;
          `}
        >
          <div ref={ref} tabIndex={0} {...rest}>
            <Link
              color="neutral100"
              onClick={onAttackerClick}
              className={css`
                display: block;
                ${isActiveEvent
                  ? `font-weight: ${theme.fontWeights.primary.bold} !important`
                  : ''};
              `}
              children={
                sourceName ? (
                  sourceName
                ) : sourceIP ? (
                  sourceIP
                ) : (
                  <FormattedMessage id="title_attackStatusUnknown" />
                )
              }
            />
          </div>
          <Tags tags={tags} />
        </div>
        {!!sourceName && !!sourceIP && (
          <div
            className={cx(
              readOnlyGridCss,
              css`
                align-self: flex-start;
                ${tags.length === 0 && 'padding-top: 3px'};
              `
            )}
          >
            {sourceIP}
          </div>
        )}
      </div>
    )
  }
)

const tagsContainerCss = css`
  display: flex;
  margin-left: 4px;
`

function Tags({ tags = [] }: { readonly tags: string[] }) {
  return (
    <div className={tagsContainerCss}>
      {[...tags].sort().map((t, i) => (
        <Tag key={i}>{t}</Tag>
      ))}
    </div>
  )
}

export function TooltipContent({
  attackType,
  attackers
}: {
  readonly attackType: AttackType
  readonly attackers: string[]
}) {
  return (
    <TooltipList>
      <li>
        <ContrastFormattedMessage
          id="title_type"
          children={(typeMsg) => (
            <TooltipLabel
              label={typeMsg}
              renderValue={() => (
                <FormattedMessage
                  id={
                    attackType === AttackType.AUTOMATED
                      ? 'detail_attacks_automatedScan'
                      : 'detail_attacks_manualAttack'
                  }
                />
              )}
            />
          )}
        />
      </li>
      <li>
        <ContrastFormattedMessage
          id="plural_users"
          values={{ count: attackers.length }}
          children={(userMsg) => (
            <TooltipLabel
              label={userMsg}
              renderValue={() =>
                attackers.length === 0 ? (
                  <FormattedMessage id="title_attackStatusUnknown" />
                ) : (
                  <span>{attackers.join(', ')}</span>
                )
              }
            />
          )}
        />
      </li>
    </TooltipList>
  )
}
